import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate, Link } from "react-router-dom"; // AJOUT de Link
import AnimalFormModal from "./AnimalFormModal";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "./ui/table";
import { Button } from "./ui/button";
import { FiBell } from "react-icons/fi";
import { GiHorseHead } from "react-icons/gi";
import { PiFarm } from "react-icons/pi";
import { BsPersonFill } from "react-icons/bs";

function calculerAge(dateStr) {
  if (!dateStr) return "-";
  const naissance = new Date(dateStr);
  const diff = Date.now() - naissance.getTime();
  const age = new Date(diff).getUTCFullYear() - 1970;
  return age >= 0 && age < 100 ? age : "-";
}

const surveillanceColors = { vert:"#22c55e", jaune:"#facc15", orange:"#fb923c", rouge:"#ef4444" };

function faded(hex, alpha = 0.13) {
  if (!hex) return undefined;
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map(x => x + x).join("");
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

// Utilitaires
const isHex = (s) => typeof s === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s);

// Couleurs par défaut si Firebase n'est pas disponible
const DEFAULT_TAG_COLORS = [
  { id: 1, nom: 'Bleu', codeHex: '#2563eb' },
  { id: 2, nom: 'Rouge', codeHex: '#ef4444' },
  { id: 3, nom: 'Vert', codeHex: '#22c55e' },
  { id: 4, nom: 'Jaune', codeHex: '#eab308' },
  { id: 5, nom: 'Orange', codeHex: '#f97316' },
  { id: 6, nom: 'Violet', codeHex: '#8b5cf6' },
  { id: 7, nom: 'Rose', codeHex: '#ec4899' },
  { id: 8, nom: 'Turquoise', codeHex: '#06b6d4' },
  { id: 9, nom: 'Gris', codeHex: '#6b7280' },
  { id: 10, nom: 'Noir', codeHex: '#111827' },
  { id: 11, nom: 'Marron', codeHex: '#92400e' },
  { id: 12, nom: 'Blanc', codeHex: '#f3f4f6' }
];

export default function AnimalList() {
  const navigate = useNavigate();
  const [animaux, setAnimaux] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [tagColors, setTagColors] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filtre, setFiltre] = useState("");
  const [tri, setTri] = useState({ col: "nom", dir: "asc" });

  async function fetchAnimaux() {
    const snap = await getDocs(collection(db, "animaux"));
    setAnimaux(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }

  async function fetchProprietaires() {
    const snap = await getDocs(collection(db, "proprietaires"));
    setProprietaires(snap.docs.map(doc => ({
      id: doc.id,
      nom: doc.data().nom,
      prenom: doc.data().prenom,
      tagCouleur: doc.data().tagCouleur,
      tagCouleurNom: doc.data().tagCouleurNom
    })));
  }

  async function fetchLieux() {
    const snap = await getDocs(collection(db, "lieux"));
    setLieux(snap.docs.map(d => ({ id: d.id, name: d.data().name })));
  }

  async function fetchTagColors() {
    try {
      // Charger les couleurs depuis la collection tagCouleursAnimaux
      const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
      let colorsData = [];
      
      tagColorsSnap.forEach(doc => {
        const data = doc.data();
        if (data.id && data.codeHex) {
          colorsData.push({ 
            id: data.id,
            nom: data.nom || '',
            codeHex: data.codeHex,
            ordre: data.ordre || data.id
          });
        }
      });

      // Trier par ordre
      colorsData.sort((a, b) => a.ordre - b.ordre);

      // Si pas de données Firebase, utiliser les couleurs par défaut
      if (colorsData.length === 0) {
        console.log('Aucune couleur trouvée dans tagCouleursAnimaux, utilisation des couleurs par défaut');
        colorsData = DEFAULT_TAG_COLORS;
      }

      setTagColors(colorsData);
    } catch (error) {
      console.error('Erreur lors du chargement des couleurs depuis tagCouleursAnimaux:', error);
      // En cas d'erreur, utiliser les couleurs par défaut
      setTagColors(DEFAULT_TAG_COLORS);
    }
  }

  useEffect(() => {
    fetchAnimaux();
    fetchProprietaires();
    fetchLieux();
    fetchTagColors();
  }, []);

  function handleEdit(animal) { setSelected(animal); }
  
  // MODIFICATION: Cette fonction ne sera plus utilisée pour les équidés
  function handleAdd() { setSelected({}); }
  
  function handleClose() { setSelected(null); fetchAnimaux(); }

  const animauxFiltres = filtre ? animaux.filter(a => a.type === filtre) : animaux;

  function trier(liste) {
    return [...liste].sort((a, b) => {
      if (!a[tri.col]) return 1;
      if (!b[tri.col]) return -1;
      if (a[tri.col] < b[tri.col]) return tri.dir === "asc" ? -1 : 1;
      if (a[tri.col] > b[tri.col]) return tri.dir === "asc" ? 1 : -1;
      return 0;
    });
  }

  function displayProprio(id) {
    const p = proprietaires.find(x => x.id === id);
    return p ? (p.nom || "-") : "-";
  }

  // Fonction pour obtenir la couleur du tag propriétaire
  function getProprietaireTagColor(proprietaireId) {
    const p = proprietaires.find(x => x.id === proprietaireId);
    if (!p) return null;
    
    // Nouveau système avec tagCouleur (ID)
    if (p.tagCouleur) {
      const numericId = parseInt(p.tagCouleur);
      if (!isNaN(numericId)) {
        const found = tagColors.find(tc => tc.id === numericId);
        if (found) return found.codeHex;
      }
    }
    
    // Ancien système avec tagCouleurNom (pour rétrocompatibilité)
    if (p.tagCouleurNom) {
      const found = tagColors.find(tc => tc.nom === p.tagCouleurNom);
      if (found) return found.codeHex;
    }
    
    return null;
  }
  
  function displayLieuName(zoneId) {
    if (!zoneId) return "-";
    const l = lieux.find(x => x.id === zoneId);
    return l ? l.name : "-";
  }

  // Fonction mise à jour pour résoudre la couleur d'un tag avec les IDs
  function tagColorHex(tagId) {
    if (!tagId) return null;
    
    // Si c'est déjà un code hex, l'utiliser directement (rétrocompatibilité)
    if (isHex(tagId)) return tagId;
    
    // Chercher par ID numérique (tagId peut être string ou number)
    const numericId = parseInt(tagId);
    if (!isNaN(numericId)) {
      const found = tagColors.find(tc => tc.id === numericId);
      if (found) return found.codeHex;
    }
    
    // Si c'est encore un nom de couleur (rétrocompatibilité)
    const foundByName = tagColors.find(tc => tc.nom === tagId);
    if (foundByName) return foundByName.codeHex;
    
    return null;
  }

  // Fonction pour obtenir le nom du tag à afficher dans le title
  function getTagDisplayName(tagId) {
    if (!tagId) return '';
    
    // Si c'est un hex direct
    if (isHex(tagId)) return tagId;
    
    // Chercher par ID
    const numericId = parseInt(tagId);
    if (!isNaN(numericId)) {
      const found = tagColors.find(tc => tc.id === numericId);
      if (found) return `${found.nom} (ID: ${found.id})`;
    }
    
    // Fallback
    return tagId;
  }

function surveillanceEtatColor(animal) {
  // Priorité au nouveau format (s minuscule), sinon ancien format (S majuscule)
  const etat = animal.surveillanceEtat || animal.SurveillanceEtat;
  return surveillanceColors[etat] || "#e5e7eb";
}

  return (
    <div className="w-full mt-4">
     {/* SECTION MODIFIÉE: Boutons d'ajout différenciés */}
<div className="mb-4 flex gap-3">
  <Link to="/animaux/nouveau">
    <Button variant="default" className="bg-gray-800 hover:bg-gray-900 text-white">
      <GiHorseHead className="mr-2 h-4 w-4" />
      + Nouveau cheval
    </Button>
  </Link>
  <Button onClick={handleAdd} variant="outline">
    <PiFarm className="mr-2 h-4 w-4" />
    + Autre animal
  </Button>
</div>
      <div className="mb-4 flex items-center gap-4">
        <span className="text-sm">Filtrer :</span>
        <Button onClick={() => setFiltre("")} variant={filtre === "" ? "default" : "secondary"}>Tous</Button>
        <Button onClick={() => setFiltre("equide")} variant={filtre === "equide" ? "default" : "secondary"}>Équidés</Button>
        <Button onClick={() => setFiltre("animal")} variant={filtre === "animal" ? "default" : "secondary"}>Autres animaux</Button>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <Table className="w-full min-w-full text-sm">
          <colgroup>
  <col className="w-auto min-w-[140px]"/>
  <col className="w-20"/>
  <col className="w-auto min-w-[120px]"/>
  <col className="w-auto min-w-[80px]"/>
  <col className="w-20"/>
  <col className="w-20"/>
  <col className="w-24"/>
  <col className="w-auto min-w-[80px]"/>
  <col className="w-20"/>
  <col className="w-auto min-w-[120px]"/>
  <col className="w-32"/>
</colgroup>
<TableHeader>
  <TableRow>
    <TableHead className="p-3 text-left uppercase font-medium cursor-pointer whitespace-nowrap"
      onClick={() => setTri({ col: "nom", dir: tri.col === "nom" && tri.dir === "asc" ? "desc" : "asc" })}>
      Nom {tri.col === "nom" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-center uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "type", dir: tri.col === "type" && tri.dir === "asc" ? "desc" : "asc" })}>
      Type {tri.col === "type" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-left uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "proprietaire", dir: tri.col === "proprietaire" && tri.dir === "asc" ? "desc" : "asc" })}>
      Propriétaire {tri.col === "proprietaire" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-left uppercase font-medium">SIRE</TableHead>
    <TableHead className="p-3 text-center uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "dateNaissance", dir: tri.col === "dateNaissance" && tri.dir === "asc" ? "desc" : "asc" })}>
      Âge {tri.col === "dateNaissance" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-center uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "sexe", dir: tri.col === "sexe" && tri.dir === "asc" ? "desc" : "asc" })}>
      Sexe {tri.col === "sexe" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-left uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "categorie", dir: tri.col === "categorie" && tri.dir === "asc" ? "desc" : "asc" })}>
      Cat. {tri.col === "categorie" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-left uppercase font-medium">Couleur</TableHead>
    <TableHead className="p-3 text-center uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "tag", dir: tri.col === "tag" && tri.dir === "asc" ? "desc" : "asc" })}>
      Tag {tri.col === "tag" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-left uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "zoneId", dir: tri.col === "zoneId" && tri.dir === "asc" ? "desc" : "asc" })}>
      Localisation {tri.col === "zoneId" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
    <TableHead className="p-3 text-center uppercase font-medium cursor-pointer"
      onClick={() => setTri({ col: "SurveillanceEtat", dir: tri.col === "SurveillanceEtat" && tri.dir === "asc" ? "desc" : "asc" })}>
      Surveillance {tri.col === "SurveillanceEtat" ? (tri.dir === "asc" ? "▲" : "▼") : ""}
    </TableHead>
  </TableRow>
</TableHeader>

          <TableBody>
            {trier(animauxFiltres).map(a => {
              const hex = tagColorHex(a.tag);
              const bgTag = faded(hex, 0.13);
const etatColor = surveillanceEtatColor(a);
              const tagDisplayName = getTagDisplayName(a.tag);
              
              return (
                <TableRow 
                  key={a.id} 
                  className="border-t hover:bg-gray-50 cursor-pointer transition-colors" 
                  style={{ background: bgTag }}
                  onClick={() => navigate(`/animaux/${a.id}`)}
                  title="Cliquer pour voir les détails"
                >
                  <TableCell className="p-3 font-medium">{a.nom}</TableCell>
                  <TableCell className="p-3 text-center">
                    {a.type === "equide" ? <GiHorseHead title="Équidé" className="h-4 w-4 mx-auto" /> : <PiFarm title="Autre animal" className="h-4 w-4 mx-auto" />}
                  </TableCell>
                  <TableCell className="p-3">
                    <div className="flex items-center gap-2">
                      {getProprietaireTagColor(a.proprietaire) && (
                        <BsPersonFill 
                          className="w-3 h-3 flex-shrink-0" 
                          style={{ color: getProprietaireTagColor(a.proprietaire) }}
                          title="Tag couleur propriétaire"
                        />
                      )}
                      <span className="truncate">{displayProprio(a.proprietaire)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="p-3">
                    <span className="truncate block max-w-[100px]" title={a.sire}>
                      {a.sire || "-"}
                    </span>
                  </TableCell>
                  <TableCell className="p-3 text-center">{calculerAge(a.dateNaissance)}</TableCell>
                  <TableCell className="p-3 text-center">
                    {(a.sexe || "-").toString().toLowerCase() === "hongre" ? "H" : (a.sexe ? a.sexe.charAt(0).toUpperCase() : "-")}
                  </TableCell>
                  <TableCell className="p-3">
                    <span className="truncate block" title={a.categorie}>
                      {a.categorie || "-"}
                    </span>
                  </TableCell>
                  <TableCell className="p-3">
                    <span className="truncate block" title={a.couleur}>
                      {a.couleur || "-"}
                    </span>
                  </TableCell>
                  <TableCell className="p-3 text-center">
                    {hex ? (
                      <span 
                        className="inline-block w-4 h-4 rounded-full border border-gray-300 mx-auto" 
                        style={{ background: hex }} 
                        title={tagDisplayName}
                      />
                    ) : (
                      a.tag && (
                        <span 
                          className="text-xs text-gray-500"
                          title={`Tag: ${a.tag} (couleur non trouvée)`}
                        >
                          {a.tag}
                        </span>
                      )
                    )}
                  </TableCell>
                  <TableCell className="p-3">
                    <span className="truncate block" title={displayLieuName(a.zoneId)}>
                      {displayLieuName(a.zoneId)}
                    </span>
                  </TableCell>
                  <TableCell className="p-3 text-center">
{(a.surveillanceEtat || a.SurveillanceEtat) && (
  <span
    className="inline-block w-12 h-4 rounded-md text-white text-xs leading-4 text-center"
    style={{ background: etatColor }}
    title={a.surveillanceEtat || a.SurveillanceEtat}
  >
  </span>
)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* MODIFICATION: Le modal ne s'ouvre que pour les "autres animaux" */}
      {selected && Object.keys(selected).length > 0 && (
        <AnimalFormModal animal={selected} onClose={handleClose} />
      )}
    </div>
  );
}